import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional callback to log the error to a monitoring service. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

/**
 * Route-level error boundary. Catches any render-time crash in a sub-tree
 * (React #31 "Objects are not valid as a React child" is the classic one)
 * and shows a legible fallback instead of blanking the whole page.
 *
 * Wrap around routes that render user-supplied or seed data — a single
 * malformed field should not take down the app.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] caught:', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  private handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div style={{
          maxWidth: 640, margin: '80px auto', padding: 32,
          background: 'var(--gradient-surface, rgba(255,255,255,0.02))',
          border: '1px solid var(--glass-border, rgba(255,255,255,0.08))',
          borderRadius: 8, color: 'var(--color-text, #eee)',
        }}>
          <h2 style={{ marginTop: 0, fontSize: 20, fontWeight: 700 }}>
            Something broke on this page.
          </h2>
          <p style={{ color: 'var(--color-text-muted, #999)', fontSize: 14, lineHeight: 1.6 }}>
            The error was logged. You can try again, go back, or reload.
            If it keeps happening, share the message below with support.
          </p>
          <pre style={{
            marginTop: 16, padding: 12, background: 'rgba(0,0,0,0.3)',
            borderRadius: 4, fontSize: 11, overflow: 'auto', maxHeight: 200,
            color: '#f88',
          }}>
            {this.state.error.name}: {this.state.error.message}
          </pre>
          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <button onClick={this.handleReset} style={{
              padding: '8px 16px', background: 'var(--color-primary, #F2C200)',
              color: '#000', border: 'none', borderRadius: 4,
              fontWeight: 600, cursor: 'pointer', fontSize: 13,
            }}>Try again</button>
            <button onClick={() => window.history.back()} style={{
              padding: '8px 16px', background: 'transparent',
              color: 'var(--color-text, #eee)',
              border: '1px solid var(--glass-border, rgba(255,255,255,0.15))',
              borderRadius: 4, cursor: 'pointer', fontSize: 13,
            }}>Go back</button>
            <button onClick={() => window.location.reload()} style={{
              padding: '8px 16px', background: 'transparent',
              color: 'var(--color-text, #eee)',
              border: '1px solid var(--glass-border, rgba(255,255,255,0.15))',
              borderRadius: 4, cursor: 'pointer', fontSize: 13,
            }}>Reload</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
